import { useCallback } from 'react';

import { api, type ClientRow, type Summary } from './api';
import { Card, Cell, Kpi, Row, Spinner, Table } from './components/Ui';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { hours, marginPercent, money, moneyCompact } from './format';
import { Shell, type ShellProps } from './Shell';

interface Data {
  readonly summary: Summary;
  readonly clients: readonly ClientRow[];
}

export function Clients(props: ShellProps) {
  const { navigate } = props;
  const tf = useTimeFilter();

  const { data, error } = useAsyncData<Data>(
    () => {
      const dateRange = tf.toDateRange();
      return Promise.all([api.summary(dateRange), api.clients(dateRange)]).then(
        ([summary, clients]) => ({ summary, clients }),
      );
    },
    [tf.selection],
    'Could not load client data.',
  );

  const accessor = useCallback((row: ClientRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      contractors: row.contractors,
      hours: row.hours,
      billed: row.billed,
      grossProfit: row.grossProfit,
      margin: marginPercent(row.grossProfit, row.billed),
      paymentTerms: row.paymentTerms,
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(data?.clients ?? [], accessor);

  if (error !== null) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props}>
        <Card>
          <Spinner label="Loading client data…" />
        </Card>
      </Shell>
    );
  }

  const { clients } = data;
  const totalBilled = clients.reduce((s, c) => s + Number(c.billed), 0).toFixed(4);
  const totalGp = clients.reduce((s, c) => s + Number(c.grossProfit), 0).toFixed(4);

  return (
    <Shell {...props}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Client Directory</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Revenue, gross profit, and payment terms by customer
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Total customers" value={String(clients.length)} delay={0} />
        <Kpi
          label="Total billed"
          value={moneyCompact(totalBilled)}
          exact={money(totalBilled)}
          tone="revenue"
          delay={50}
        />
        <Kpi
          label="Total GP"
          value={moneyCompact(totalGp)}
          exact={money(totalGp)}
          tone="profit"
          delay={100}
        />
        <Kpi
          label="Avg GP margin"
          value={marginPercent(totalGp, totalBilled)}
          tone="profit"
          delay={150}
        />
      </div>

      <Card className="mt-4" delay={200}>
        <Table
          head={[
            { label: 'Customer', sortKey: 'name' },
            { label: 'Contractors', align: 'right', sortKey: 'contractors' },
            { label: 'Hours', align: 'right', sortKey: 'hours' },
            { label: 'Billed', align: 'right', sortKey: 'billed' },
            { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
            { label: 'Margin', align: 'right', sortKey: 'margin' },
            { label: 'Payment terms', align: 'right', sortKey: 'paymentTerms' },
          ]}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          {sorted.map((row) => (
            <Row key={row.id} onClick={() => navigate(`client/${row.id}`)}>
              <Cell>
                <span className="cursor-pointer text-ink transition-colors hover:text-profit">
                  {row.name}
                </span>
              </Cell>
              <Cell align="right" mono>
                {row.contractors}
              </Cell>
              <Cell align="right" mono>
                {hours(row.hours)}
              </Cell>
              <Cell align="right" mono>
                <span className="text-revenue">{money(row.billed)}</span>
              </Cell>
              <Cell align="right" mono>
                <span className="text-profit">{money(row.grossProfit)}</span>
              </Cell>
              <Cell align="right" mono muted>
                {marginPercent(row.grossProfit, row.billed)}
              </Cell>
              <Cell align="right" muted>
                {row.paymentTerms ?? '—'}
              </Cell>
            </Row>
          ))}
        </Table>
      </Card>
    </Shell>
  );
}
